import { zodResolver } from "@hookform/resolvers/zod";
import { AuthController } from "@somewear/auth";
import { Form, FormField, RenderIf } from "@somewear/ui";
import { CenterContent, ErrorMessage } from "@web/common/Common";
import * as Common from "@web/common/Common";
import Loader from "@web/common/Loader";
import Paths from "@web/common/Paths";
import Config from "@web/config/Config";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { forkJoin } from "rxjs";

import * as LandingCommon from "../LandingCommon";
import type { UserSignupSchema } from "./signup.schema";
import { getUserSignupSchema } from "./signup.schema";

interface IProps {
	isSubmitting: boolean;
	setIsSubmitting: (isSubmitting: boolean) => void;
}

const AuthErrors = {
	EmailAlreadyInUse: "auth/email-already-in-use",
	WeakPassword: "auth/weak-password",
} as const;

const AuthMessages = {
	[AuthErrors.EmailAlreadyInUse]: "This email is already in use.",
	[AuthErrors.WeakPassword]: "Password must be at least 6 characters long.",
} as const;

const SignUpView: React.FC<IProps> = (props) => {
	const userSignupSchema = getUserSignupSchema(Config.firebase.enable);

	const form = useForm<UserSignupSchema>({
		resolver: zodResolver(userSignupSchema),
		defaultValues: {
			email: "",
			password: "",
			termsAccepted: false,
			...(Config.firebase.enable ? { fname: "", lname: "" } : {}),
		},
		mode: "onSubmit",
		reValidateMode: "onSubmit",
	});

	const onSubmit = ({ email, password, fname, lname }: UserSignupSchema) => {
		form.clearErrors();
		props.setIsSubmitting(true);

		AuthController.service?.createUserWithEmailAndPassword$({ email, password }).subscribe({
			next: (user) => {
				if (user === undefined) {
					form.setError("root", { message: "Failed to create a user" });
					return;
				}

				if (Config.firebase.enable) {
					forkJoin([
						AuthController.service.sendEmailVerification$(),
						AuthController.service.updateProfile$(`${fname} ${lname}`),
					]).subscribe({
						next: async () => {
							console.log("Email sent and profile updated");
							props.setIsSubmitting(false);
						},
						error: (error) => {
							console.log("Error in email verification or profile update");
							console.log(error);
							props.setIsSubmitting(false);
						},
					});
				} else {
					props.setIsSubmitting(false);
				}
			},
			error: (error) => {
				props.setIsSubmitting(false);
				if (AuthMessages[error.code as keyof typeof AuthMessages]) {
					console.log("firebase error");
					error.message = AuthMessages[error.code as keyof typeof AuthMessages];
					form.setError("root", { message: error.message });
					return;
				} else {
					console.log(error);
				}

				form.setError("root", { message: "Failed to create a user" });
			},
		});
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.key === "Enter") {
			form.handleSubmit(onSubmit)();
		}
	};

	if (props.isSubmitting) return <Loader />;

	return (
		<div className="mx-2 w-full md:w-auto md:min-w-96">
			<LandingCommon.CenterHeader text="Create a Somewear account" />
			<div className="my-2">
				<ErrorMessage
					error={Object.values(form.formState.errors)
						.map((e) => ({ message: e.message || "" }))
						.filter((e) => !!e?.message)}
				/>
			</div>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
					<div className="w-full md:px-10">
						<RenderIf condition={Config.firebase.enable}>
							<div className="row gap-2">
								<div className="justify-center col-span-12 md:col-span-6">
									<FormField
										control={form.control}
										name="fname"
										render={({ field }) => (
											<LandingCommon.InputView
												type="text"
												placeholder="First name"
												{...field}
											/>
										)}
									/>
								</div>
								<div className="justify-center col-span-12 md:col-span-6">
									<FormField
										control={form.control}
										name="lname"
										render={({ field }) => (
											<LandingCommon.InputView
												type="text"
												placeholder="Last name"
												{...field}
											/>
										)}
									/>
								</div>
							</div>
						</RenderIf>
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<LandingCommon.InputView
									type={Config.firebase.enable ? "email" : "text"}
									className="!max-w-full"
									placeholder={
										Config.firebase.enable ? "Email" : "Email / Username"
									}
									{...field}
								/>
							)}
						/>
						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<LandingCommon.InputView
									type="password"
									placeholder="Password"
									{...field}
								/>
							)}
						/>
						<FormField
							control={form.control}
							name="termsAccepted"
							render={({ field }) => (
								<CenterContent className="mt-2 mb-3">
									<input
										type="checkbox"
										{...field}
										checked={field.value}
										value={field.value ? "checked" : ""}
										onChange={(e) => {
											field.onChange(e.target.checked);
										}}
									/>
									<label className="terms-and-conditions">
										{" "}
										I have read and accept the&nbsp;
										<span>
											<a
												href="https://www.somewearlabs.com/legal"
												target="_blank"
												rel="noopener noreferrer"
											>
												Terms and Conditions
											</a>
										</span>
										&nbsp;and&nbsp;
										<span>
											<a
												href="https://www.somewearlabs.com/legal"
												target="_blank"
												rel="noopener noreferrer"
											>
												Privacy Policy
											</a>
										</span>
									</label>
								</CenterContent>
							)}
						/>
						<Common.CenterContent>
							<LandingCommon.Button type="submit">
								<Common.TitleButtonText>Sign up</Common.TitleButtonText>
							</LandingCommon.Button>
						</Common.CenterContent>
						<Common.CenterContent>
							<Link to={Paths.Root}>
								<LandingCommon.LinkButton className="mt-2.5">
									Back
								</LandingCommon.LinkButton>
							</Link>
						</Common.CenterContent>
					</div>
				</form>
			</Form>
		</div>
	);
};

export default SignUpView;
